import { Box, Button, Flex, Heading, Spinner, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { FilterChip } from "../../common/components/FilterChip";
import { useMarkAllNotificationsRead } from "../../common/notifications/useMarkAllNotificationsRead";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bffApi, useListMyNotificationsQuery, useMarkNotificationsAsPeekedMutation, type NotificationDto } from "../../autogen/bff-api";
import { ErrorBoundary } from "../../common/errors/ErrorBoundary/ErrorBoundary";
import { useApiError } from "../../common/errors/useApiError";
import { useAppDispatch } from "../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { NotificationRow } from "../../common/notifications/NotificationRow";

const LIMIT = 20;

type View = "all" | "unread";

export const NotificationsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const [view, setView] = useState<View>("all");
  const unreadOnly = view === "unread" ? { unread: true } : {};
  const { currentData, isLoading, isFetching, isError } = useListMyNotificationsQuery({ limit: LIMIT, offset: 0, ...unreadOnly });
  const [markAsPeeked] = useMarkNotificationsAsPeekedMutation();
  const [extra, setExtra] = useState<NotificationDto[]>([]);
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(false);

  const firstPage = currentData?.notifications ?? [];
  const firstPageIds = new Set(firstPage.map((notification) => notification.id));
  const loaded = [...firstPage, ...extra.filter((notification) => !firstPageIds.has(notification.id))];
  const all = view === "unread" ? loaded.filter((notification) => !notification.viewed) : loaded;
  const showsSpinner = isLoading || (isFetching && !currentData);

  const switchTo = (next: View) => {
    setView(next);
    setExtra([]);
    setExhausted(false);
  };
  const { unreadCount, markAll, isMarking } = useMarkAllNotificationsRead();

  const markAllRead = async () => {
    try {
      await markAll();
      setExtra((loaded) => loaded.map((notification) => ({ ...notification, viewed: true })));
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const peekUnseen = useCallback(
    (items: NotificationDto[]) => {
      const ids = items.filter((n) => !n.peeked).map((n) => n.id);
      // Background bookkeeping: swallow failures (no toast) but consume the promise so a rejection isn't unhandled.
      if (ids.length > 0) void markAsPeeked({ markNotificationsRequest: { notificationIds: ids } }).unwrap().catch(() => undefined);
    },
    [markAsPeeked]
  );

  useEffect(() => {
    if (currentData?.notifications) peekUnseen(currentData.notifications);
    // Keyed on the page-1 query result; peeking later pages happens in loadMore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatch(
        bffApi.endpoints.listMyNotifications.initiate({ limit: LIMIT, offset: all.length, ...(view === "unread" ? { unread: true } : {}) }),
      ).unwrap();
      if (res.notifications.length < LIMIT) setExhausted(true);
      setExtra((prev) => [...prev, ...res.notifications]);
      peekUnseen(res.notifications);
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setLoading(false);
    }
  }, [dispatch, all.length, displayer, peekUnseen, view]);

  return (
    <Box maxW="container.md" mx="auto">
      <Flex align="center" justify="space-between" gap="3" mb="6" wrap="wrap">
        <Heading as="h1" size="lg" color="smText">{t("Alerts")}</Heading>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" colorScheme="teal" onClick={markAllRead} isLoading={isMarking}>
            {t("Mark all as read")}
          </Button>
        )}
      </Flex>
      <Wrap spacing="2" mb="4" role="group" aria-label={t("Show alerts") ?? undefined}>
        <WrapItem>
          <FilterChip active={view === "all"} label={t("All")} onClick={() => switchTo("all")} />
        </WrapItem>
        <WrapItem>
          <FilterChip active={view === "unread"} label={t("Unread")} onClick={() => switchTo("unread")} />
        </WrapItem>
      </Wrap>
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
        {showsSpinner ? (
          <Flex py="16" justify="center"><Spinner color="smPrimary" /></Flex>
        ) : isError ? (
          <Flex py="16" justify="center"><Text color="smError">{t("Could not load alerts.")}</Text></Flex>
        ) : all.length === 0 ? (
          <Flex py="16" justify="center"><Text color="smMuted">{t("You're all caught up")}</Text></Flex>
        ) : (
          all.map((n, i) => (
            <Box key={n.id} borderTop={i === 0 ? undefined : "1px solid"} borderColor="smBorder">
              <ErrorBoundary
                scope={`notification-row-${n.id}`}
                fallback={<Flex px="4" py="3"><Text color="smMuted" fontSize="sm">{t("Couldn't show this alert.")}</Text></Flex>}
              >
                <NotificationRow
                  notification={n}
                  selectedOrgId={authState.selectedOrg.id}
                  onRead={(id) =>
                    setExtra((loaded) => loaded.map((item) => (item.id === id ? { ...item, viewed: true } : item)))
                  }
                  onDismissed={(id) => setExtra((loaded) => loaded.filter((item) => item.id !== id))}
                />
              </ErrorBoundary>
            </Box>
          ))
        )}
      </Box>
      {!exhausted && all.length > 0 && firstPage.length >= LIMIT && (
        <Flex justify="center" mt="4">
          <Button variant="outline" onClick={loadMore} isLoading={loading}>{t("Load more")}</Button>
        </Flex>
      )}
    </Box>
  );
};
